<template>
  <div class="supplier-order-detail-container" v-loading="loading">
    <!-- 顶部标题栏 -->
    <div class="page-header">
      <div class="header-title">
        <div class="title-mark"></div>
        <span>订单详情 - {{ orderInfo.orderTitle || orderInfo.orderId || '加载中...' }}</span>
      </div>
      <div class="header-actions">
        <el-button v-permission="'orders:supplier-order-detail:print'" type="primary" icon="el-icon-printer" @click="printOrder" :disabled="loading">打印订单</el-button>
        <el-button v-permission="'orders:supplier-order-detail:print'" icon="el-icon-download" @click="downloadExcel" :disabled="loading">下载 Excel</el-button>
        <el-button @click="goBack" icon="el-icon-back">返回</el-button>
      </div>
    </div>

    <!-- 打印内容区域 -->
    <div class="print-content" ref="printContent">
    
    <!-- 订单基本信息 -->
    <div class="info-section">
      <h3 class="section-title">订单信息</h3>
      <el-row :gutter="20">
        <el-col :span="12">
          <div class="info-item">
            <label>订单标题：</label>
            <span>{{ orderInfo.orderTitle || '未设置' }}</span>
          </div>
        </el-col>
        <el-col :span="12">
          <div class="info-item">
            <label>订单编号：</label>
            <span>{{ orderInfo.orderId || orderInfo.id || '未生成' }}</span>
          </div>
        </el-col>
        <el-col :span="12">
          <div class="info-item">
            <label>订单状态：</label>
            <span :class="'status-text status-' + orderInfo.status">{{ orderInfo.status | formatStatus }}</span>
          </div>
        </el-col>
        <el-col :span="12">
          <div class="info-item">
            <label>订单类型：</label>
            <span :class="'order-type-text order-type-' + orderInfo.orderType">{{ getOrderTypeText(orderInfo.orderType) }}</span>
          </div>
        </el-col>
        <el-col :span="12">
          <div class="info-item">
            <label>创建时间：</label>
            <span>{{ formatDate(orderInfo.createTime) }}</span>
          </div>
        </el-col>
        <el-col :span="12">
          <div class="info-item">
            <label>创建人：</label>
            <span>{{ maskName(orderInfo.creatorName) || '--' }}</span>
          </div>
        </el-col>
        <el-col :span="12">
          <div class="info-item">
            <label>合同名称：</label>
            <span>{{ getContractNames() }}</span>
          </div>
        </el-col>
        <el-col :span="12">
          <div class="info-item">
            <label>合同编号：</label>
            <span>{{ getContractNumbers() }}</span>
          </div>
        </el-col>
      </el-row>
    </div>

    <!-- 单位信息 -->
    <div class="info-section">
      <h3 class="section-title">单位信息</h3>
      <el-row :gutter="20">
        <!-- 需求单位信息（左边） -->
        <el-col :span="12">
          <div class="unit-info-block demand-unit">
            <h4 class="unit-title">需求单位</h4>
            <div class="info-item">
              <label>单位名称：</label>
              <span>{{ getDemandUnits() }}</span>
            </div>
            <div class="info-item">
              <label>二级机构：</label>
              <span>{{ getSecondaryOrgNames() }}</span>
            </div>
            <div class="info-item">
              <label>联系人：</label>
              <span>{{ maskName(orderInfo.demandContactPerson || orderInfo.contactPerson) || '--' }}</span>
            </div>
            <div class="info-item">
              <label>联系电话：</label>
              <span>{{ maskPhone(orderInfo.demandContactPhone || orderInfo.contactPhone) || '--' }}</span>
            </div>
            <div class="info-item">
              <label>收货地址：</label>
              <span>{{ getDeliveryAddress() }}</span>
            </div>
          </div>
        </el-col>
        
        <!-- 供应商单位信息（右边） -->
        <el-col :span="12">
          <div class="unit-info-block supplier-unit">
            <h4 class="unit-title">供应商单位</h4>
            <div class="info-item">
              <label>单位名称：</label>
              <span>{{ orderInfo.supplierDetails && orderInfo.supplierDetails.unitName || orderInfo.supplierName || '--' }}</span>
            </div>
            <div class="info-item">
              <label>联系人：</label>
              <span>{{ maskName(orderInfo.contactPerson) || '--' }}</span>
            </div>
            <div class="info-item">
              <label>联系电话：</label>
              <span>{{ maskPhone(orderInfo.contactPhone) || '--' }}</span>
            </div>
          </div>
        </el-col>
      </el-row>
    </div>

    
    <!-- 订单产品清单 -->
    <div class="info-section">
      <h3 class="section-title">产品清单</h3>
      <div v-if="orderMaterials && orderMaterials.length > 0">
        <el-table
          :data="orderMaterials"
          border
          fit
          stripe
          highlight-current-row
          style="width: 100%"
          :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
          class="materials-table"
        >
          <el-table-column type="index" label="序号" width="60" align="center"></el-table-column>
          <el-table-column prop="productName" label="材料名称" min-width="120"></el-table-column>
          <el-table-column prop="specification" label="规格型号" width="150">
            <template slot-scope="scope">
              <el-tooltip
                v-if="scope.row.specification"
                :content="scope.row.specification"
                placement="top"
                effect="dark"
                popper-class="spec-tooltip"
              >
                <span>{{ truncateText(scope.row.specification, 20) }}</span>
              </el-tooltip>
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column prop="materialRequirement" label="材质要求" width="120"></el-table-column>
          <el-table-column prop="unit" label="单位" width="80" align="center"></el-table-column>
          <el-table-column prop="quantity" label="数量" width="120" align="center">
            <template slot-scope="scope">
              {{ scope.row.quantity }}
            </template>
          </el-table-column>
          <!-- 固定价合同显示合同价 -->
          <el-table-column label="合同价" width="120" align="center" v-if="!hasFloatingPriceContract">
            <template slot-scope="scope">
              {{ formatUnitPrice(scope.row.taxInclusiveUnitPrice) }}
            </template>
          </el-table-column>
          <!-- 变动价合同显示含税固定价 -->
          <el-table-column label="含税固定价(元)" width="120" align="center" v-if="hasFloatingPriceContract">
            <template slot-scope="scope">
              {{ formatAmount(scope.row.taxInclusiveBasePrice) }}
            </template>
          </el-table-column>
          <!-- 变动价合同显示含税变动价 -->
          <el-table-column label="含税变动价(元)" width="120" align="center" v-if="hasFloatingPriceContract">
            <template slot-scope="scope">
              {{ formatAmount(scope.row.taxInclusiveFloatingPrice) }}
            </template>
          </el-table-column>
          <el-table-column label="到货日期" width="150" align="center">
            <template slot-scope="scope">
              {{ scope.row.deliveryDate || '-' }}
            </template>
          </el-table-column>
          <el-table-column prop="remark" label="备注" min-width="120">
            <template slot-scope="scope">
              {{ scope.row.remark || '-' }}
            </template>
          </el-table-column>
        </el-table>
        
        <!-- 合计信息 -->
        <div class="total-info" v-if="orderMaterials.length > 0">
          <span>合计数量: {{ formatQuantity(totalQuantity) }}</span>
          <span style="margin-left: 20px;">合计金额: {{ formatAmount(totalPrice) }} 元</span>
          <!-- 多合同订单时显示合同信息 -->
          <div v-if="orderInfo.contractDetails && orderInfo.contractDetails.length > 1" class="contract-info">
            <span>涉及合同: {{ orderInfo.contractDetails.length }} 个</span>
          </div>
        </div>
      </div>
      <div v-else class="no-data">
        <el-empty description="暂无产品清单数据"></el-empty>
      </div>
    </div>
    
    <!-- 订单操作区域 -->
    <div class="info-section no-print">
      <h3 class="section-title">订单操作</h3>
      <div class="action-buttons">
        <!-- 退货订单确认操作 -->
        <template v-if="isReturnOrder() && orderInfo.status === 'PENDING' && isSupplier()">
          <el-button v-permission="'orders:supplier-order-detail:confirm-return'" type="success" @click="confirmReturnOrder">确认退货订单</el-button>
          <el-button type="danger" @click="rejectReturnOrder">拒绝退货</el-button>
        </template>
        
        <!-- 退货订单已确认状态 - 进行中 -->
        <template v-else-if="isReturnOrder() && orderInfo.status === 'EXECUTING' && isSupplier()">
          <el-button type="primary" @click="showShipmentDialog">发货登记</el-button>
        </template>
        
        <!-- 退货订单已发货状态 -->
        <template v-else-if="isReturnOrder() && orderInfo.status === 'SENDING'">
          <el-button type="warning" @click="goToShipmentPage">查看发货记录</el-button>
        </template>
        
        <!-- 退货订单其他状态 -->
        <template v-else-if="isReturnOrder()">
          <div class="status-info">
            <i class="el-icon-info"></i>
            <span>退货订单状态：{{ orderInfo.status | formatStatus }}</span>
          </div>
        </template>
        
        <!-- 正常订单 - 待确认状态，且当前用户是供应商 -->
        <template v-else-if="orderInfo.status === 'ACTIVE' && isSupplier()">
          <el-button type="success" @click="updateOrderStatus('EXECUTING')">接受订单</el-button>
          <el-button type="danger" @click="updateOrderStatus('REJECTED')">拒绝订单</el-button>
        </template>
        
        <!-- 正常订单 - 进行中状态，且当前用户是供应商 -->
        <template v-else-if="orderInfo.status === 'EXECUTING' && isSupplier()">
          <el-button type="primary" @click="showShipmentDialog">发货登记</el-button>
        </template>
        
        <!-- 正常订单 - 已发货状态 -->
        <template v-else-if="orderInfo.status === 'SENDING'">
          <el-button type="warning" @click="goToShipmentPage">查看发货记录</el-button>
        </template>
        
        <!-- 用户不是供应商或其他状态 -->
        <template v-else>
          <div class="status-info">
            <i class="el-icon-info"></i>
            <span>当前订单状态为 {{ orderInfo.status | formatStatus }}，无可用操作</span>
          </div>
        </template>
      </div>
    </div>
    
    <!-- 打印内容区域结束 -->
    </div>
    
    <!-- 发货登记弹窗 -->
    <ShipmentDialog
      :visible.sync="shipmentDialogVisible"
      :orderId="orderId"
      @shipment-success="handleShipmentSuccess"
    />
  </div>
</template>

<script>
import { get_order_detail, update_order_status, confirm_return_order } from '@/api/modules/orders.js';
import ShipmentDialog from '@/components/ShipmentDialog/index.vue';
import { maskName as maskNameUtil } from '@/utils/mask.js';
import { installPlatformPrintFooter } from '@/utils/print-footer';
import { downloadOrderExcel } from '@/utils/order-excel';

export default {
  name: 'SupplierOrderDetail',
  components: {
    ShipmentDialog
  },
  filters: {
    // 日期格式化过滤器
    formatDate(dateString) {
      if (!dateString) return '';
      
      try {
        const date = new Date(dateString);
        // 检查日期是否有效
        if (isNaN(date.getTime())) return dateString;
        
        // 格式化为 YYYY-MM-DD HH:MM:SS
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      } catch (error) {
        console.error('日期格式化错误:', error);
        return dateString;
      }
    },
    // 状态格式化过滤器
    formatStatus(status) {
      const statusMap = {
        'ACTIVE': '待确认',
        'EXECUTING': '进行中',
        'SENDING': '已发货',
        'RECEIVING': '已收货',
        'END': '已结束'
      };
      return statusMap[status] || status;
    }
  },
  computed: {
    // 判断是否有变动价合同
    hasFloatingPriceContract() {
      if (!this.orderMaterials || this.orderMaterials.length === 0) {
        return false;
      }
      
      // 检查订单项中是否有变动价项目（主要检查pricingMethod字段）
      return this.orderMaterials.some(item => 
        item.pricingMethod === 'FLOATING'
      );
    }
  },
  data() {
    return {
      orderId: null,
      loading: false,
      orderInfo: {},
      orderMaterials: [],
      totalQuantity: 0,
      totalPrice: 0,
      orderHistory: [],
      currentUnitId: null,
      shipmentDialogVisible: false,
    };
  },
  created() {
    // 从路由参数获取订单ID
    this.orderId = this.$route.params.id;
    if (!this.orderId) {
      this.$message.error('缺少订单ID参数');
      this.goBack();
      return;
    }
    
    // 获取当前用户的单位ID
    this.getCurrentUnitId();
    
    // 加载订单详情
    this.getOrderDetail();
  },
  methods: {
    // 截断文本显示，默认为20个字符
    truncateText(text, length = 20) {
      if (!text) return '-';
      const str = String(text);
      return str.length > length ? `${str.slice(0, length)}...` : str;
    },

    // 格式化金额
    formatAmount(amount) {
      if (!amount && amount !== 0) return '0.00';
      const num = parseFloat(amount);
      if (isNaN(num)) return '0.00';
      return num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },
    
    // 格式化单价（保留8位小数，去除末尾无意义的0）
    formatUnitPrice(amount) {
      if (!amount && amount !== 0) return '0';
      const num = parseFloat(amount);
      if (isNaN(num)) return '0';
      // 先格式化为8位小数，然后去除末尾的0
      const formatted = num.toFixed(8);
      // 去除末尾的0和小数点（如果有）
      return formatted.replace(/\.?0+$/, '') || '0';
    },
    
    // 格式化数量（保留4位小数，去除末尾无意义的0）
    formatQuantity(quantity) {
      if (quantity === undefined || quantity === null) return '0';
      const num = parseFloat(quantity);
      if (isNaN(num)) return '0';
      // 先格式化为4位小数，然后去除末尾的0和小数点（如果有）
      const formatted = num.toFixed(4);
      return formatted.replace(/\.?0+$/, '') || '0';
    },

    // 格式化日期
    formatDate(date) {
      if (!date) return '';
      try {
        const dateObj = new Date(date);
        if (isNaN(dateObj.getTime())) return date;
        
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        const hours = String(dateObj.getHours()).padStart(2, '0');
        const minutes = String(dateObj.getMinutes()).padStart(2, '0');
        const seconds = String(dateObj.getSeconds()).padStart(2, '0');
        
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      } catch (error) {
        console.error('日期格式化错误:', error);
        return date;
      }
    },

    // 格式化百分比
    formatPercentage(value) {
      if (value === null || value === undefined) return '0.00';
      return `${parseFloat(value).toFixed(2)}`;
    },
    
    // 获取当前用户的单位ID
    getCurrentUnitId() {
      try {
        // 添加防御性检查，避免访问不存在的属性
        const store = this.$store || {};
        const state = store.state || {};
        
        // 尝试从不同可能的路径获取用户信息
        let userInfo = null;
        
        if (state.user && state.user.userInfo) {
          // 如果是 state.user.userInfo 的结构
          userInfo = state.user.userInfo;
} else if (state.userInfo) {
          // 如果是 state.userInfo 的结构
          userInfo = state.userInfo;
} else if (state.auth && state.auth.userInfo) {
          // 如果是 state.auth.userInfo 的结构
          userInfo = state.auth.userInfo;
}
        
        // 如果找不到，可以考虑从localStorage获取
        if (!userInfo || !userInfo.unitId) {
          const localUserInfo = localStorage.getItem('userInfo');
          if (localUserInfo) {
            try {
              userInfo = JSON.parse(localUserInfo);
} catch (e) {
              console.error('解析localStorage中的userInfo失败:', e);
            }
          }
        }
        
if (userInfo && userInfo.unitId) {
          this.currentUnitId = userInfo.unitId;
} else {
          console.warn('未能获取unitId，将使用默认值1进行测试');
          // 使用默认值便于测试
          this.currentUnitId = 1;
        }
      } catch (err) {
        console.error('获取用户unitId出错:', err);
        console.warn('发生错误，将使用默认值1进行测试');
        // 出错时使用默认值便于测试
        this.currentUnitId = 1;
      }
    },
    
    // 检查当前用户是否是供应商
    isSupplier() {
      // 根据新的数据结构，检查当前用户ID是否与订单的creatorId匹配
return this.currentUnitId && 
             this.orderInfo.creatorId && 
             this.currentUnitId === this.orderInfo.creatorId;
    },
    
    // 检查是否为退货订单
    isReturnOrder() {
      return this.orderInfo.orderType === '02' || this.orderInfo.orderType === 'RETURN';
    },
    
    // 确认退货订单
    confirmReturnOrder() {
      this.$confirm('确认要确认这个退货订单吗？确认后将自动创建物流记录和发货清单。', '确认退货订单', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.loading = true;
        
        confirm_return_order(this.orderId)
          .then(response => {
            if (response.data && response.data.success) {
              this.$message.success('退货订单确认成功');
              
              // 更新本地订单状态
              this.orderInfo.status = 'EXECUTING';
              
              // 重新获取订单详情以获取最新状态
              this.getOrderDetail();
            } else {
              this.$message.error(response.data?.message || '退货订单确认失败');
            }
            this.loading = false;
          })
          .catch(error => {
            console.error('确认退货订单失败:', error);
            this.$message.error('确认退货订单失败: ' + (error.message || '未知错误'));
            this.loading = false;
          });
      }).catch(() => {
        this.$message.info('已取消确认操作');
      });
    },
    
    // 拒绝退货订单
    rejectReturnOrder() {
      this.$confirm('确认要拒绝这个退货订单吗？', '拒绝退货订单', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        // 这里可以调用拒绝退货的API，如果后端有提供的话
        // 目前先使用通用的状态更新方法
        this.updateOrderStatus('REJECTED');
      }).catch(() => {
        this.$message.info('已取消拒绝操作');
      });
    },
    
    // 获取需求单位名称
    getDemandUnits() {
      if (this.orderInfo.contractDetails && this.orderInfo.contractDetails.length > 0) {
        const allUnits = [];
        this.orderInfo.contractDetails.forEach(contract => {
          if (contract.demandUnits && contract.demandUnits.length > 0) {
            allUnits.push(...contract.demandUnits.map(unit => unit.unitName));
          }
        });
        const uniqueUnits = [...new Set(allUnits)];
        return uniqueUnits.length > 0 ? uniqueUnits.join('，') : '未知单位';
      }
      return this.orderInfo.customerName || this.orderInfo.creatorName || '未知单位';
    },
    
    // 获取合同名称
    getContractNames() {
      if (this.orderInfo.contractDetails && this.orderInfo.contractDetails.length > 0) {
        return this.orderInfo.contractDetails.map(contract => contract.contractName).join('，');
      }
      return this.orderInfo.contractName || '未知合同';
    },
    
    // 获取合同编号
    getContractNumbers() {
      if (this.orderInfo.contractDetails && this.orderInfo.contractDetails.length > 0) {
        return this.orderInfo.contractDetails.map(contract => contract.contractNumber || contract.id).join('，');
      }
      return this.orderInfo.contractId || '未知编号';
    },
    
    // 获取二级机构名称
    getSecondaryOrgNames() {
      if (this.orderInfo.secondaryOrgNames && this.orderInfo.secondaryOrgNames.length > 0) {
        return this.orderInfo.secondaryOrgNames.join('，');
      }
      return '--';
    },
    
    // 获取收货地址
    getDeliveryAddress() {
      const savedAddress = this.orderInfo.deliveryAddress || this.orderInfo.contactAddress;
      if (savedAddress) {
        return savedAddress;
      }

      if (this.orderInfo.secondaryOrgDetails && this.orderInfo.secondaryOrgDetails.length > 0) {
        const addresses = this.orderInfo.secondaryOrgDetails
          .map(org => org.detailAddress)
          .filter(addr => addr)
          .join('，');
        if (addresses) {
          return addresses;
        }
      }

      return '--';
    },
    
    // 获取项目名称（二级机构）- 保留兼容
    getProjectNames() {
      if (this.orderInfo.contractDetails && this.orderInfo.contractDetails.length > 0) {
        const allProjects = [];
        this.orderInfo.contractDetails.forEach(contract => {
          if (contract.projects && contract.projects.length > 0) {
            allProjects.push(...contract.projects.map(project => project.projectName));
          }
        });
        const uniqueProjects = [...new Set(allProjects)];
        return uniqueProjects.length > 0 ? uniqueProjects.join('，') : '未关联项目';
      }
      return '未关联项目';
    },
    
    // 获取订单详情
    getOrderDetail() {
      this.loading = true;
      get_order_detail(this.orderId)
        .then(response => {
          if (response.data && response.data.success) {
            const orderData = response.data.data;
// 处理合同信息 - 支持多合同
            if (orderData.contractDetails && orderData.contractDetails.length > 0) {
              // 新格式：contractDetails是数组
} else if (!orderData.contractName && orderData.CustomerContract) {
              // 旧格式：单个合同
              orderData.contractName = orderData.CustomerContract.contract_name;
            }
            
            // 供应商信息现在直接从 supplierDetails 对象获取，无需额外处理
            
            // 保存订单信息
            this.orderInfo = orderData;
            
            // 获取订单材料项
let materialsList = [];
            
            if (orderData.orderItems && Array.isArray(orderData.orderItems) && orderData.orderItems.length > 0) {
// 直接使用新的数据结构
              materialsList = orderData.orderItems;
            } else {
              console.warn('未找到材料清单数据');
              materialsList = [];
            }
            
            // 处理材料项，确保所有必需字段都存在，使用新的字段结构
            this.orderMaterials = materialsList.map((item, index) => ({
              ...item,
              sequenceNumber: item.sequenceNumber || (index + 1), // 序号
              productName: item.materialsName || item.productName || '未命名材料',
              specification: item.materialsSpec || item.specification || '',
              materialRequirement: item.materialRequirement || '',
              unit: item.unit || '个',
              quantity: item.quantity || 1,
              // 定价方式
              pricingMethod: item.pricingMethod || 'FIXED',
              // 固定价字段
              taxExclusiveUnitPrice: item.beforePrice || item.taxExclusiveUnitPrice || 0,
              taxInclusiveUnitPrice: item.price || item.taxInclusiveUnitPrice || 0,
              // 变动价字段（变动价合同需要保留含税固定价）
              taxInclusiveBasePrice: item.taxInclusiveBasePrice || null,
              taxInclusiveFloatingPrice: item.taxInclusiveFloatingPrice || null,
              taxInclusiveTotalAmount: item.taxInclusiveTotalAmount || (item.price || item.taxInclusiveUnitPrice || 0) * (item.quantity || 1), // 含税合价
              taxRate: item.taxRate || 0,
              deliveryDate: item.deliveryDate || '', // 到货日期
              remark: item.remark || '', // 备注
              supplierName: item.supplierName || ''
            }));
            
// 计算总数量和总价
            this.calculateTotals();
            
            // 获取订单历史
            if (orderData.history && Array.isArray(orderData.history)) {
              this.orderHistory = orderData.history;
            } else {
              // 如果没有历史记录，创建一个模拟的历史记录
              this.createMockHistory();
            }
          } else {
            this.$message.error(response.data?.message || '获取订单详情失败');
          }
          this.loading = false;
        })
        .catch(error => {
          console.error('获取订单详情失败:', error);
          this.$message.error('获取订单详情失败: ' + (error.message || '未知错误'));
          this.loading = false;
        });
    },
    
    // 计算材料项小计
    calculateItemTotal(item) {
      // 优先使用含税合价，如果没有则计算：含税单价 * 数量
      if (item.taxInclusiveTotalAmount) {
        return parseFloat(item.taxInclusiveTotalAmount).toFixed(2);
      }
      const price = parseFloat(item.taxInclusiveUnitPrice || item.price || 0);
      const quantity = parseFloat(item.quantity || 1);
      return (price * quantity).toFixed(2);
    },
    
    // 计算总数量和总价
    calculateTotals() {
      if (!this.orderMaterials || this.orderMaterials.length === 0) {
        this.totalQuantity = 0;
        this.totalPrice = 0;
        return;
      }
      
      // 计算总数量（支持4位小数）
      this.totalQuantity = this.orderMaterials.reduce((sum, item) => {
        return sum + (parseFloat(item.quantity) || 0);
      }, 0);
      
      // 计算总价
      this.totalPrice = this.orderMaterials.reduce((sum, item) => {
        // 优先使用含税合价，如果没有则计算：含税单价 * 数量
        if (item.taxInclusiveTotalAmount) {
          return sum + parseFloat(item.taxInclusiveTotalAmount);
        }
        const price = parseFloat(item.taxInclusiveUnitPrice || item.price || 0);
        const quantity = parseFloat(item.quantity || 1);
        return sum + (price * quantity);
      }, 0).toFixed(2);
      
      // 如果订单信息中有总价，直接使用
      if (this.orderInfo && this.orderInfo.priceTotal) {
        this.totalPrice = parseFloat(this.orderInfo.priceTotal).toFixed(2);
      }
    },
    
    // 获取状态标签类型
    getStatusType(status) {
      switch (status) {
        case 'ACTIVE':
          return 'warning';
        case 'EXECUTING':
          return 'success';
        case 'SENDING':
          return 'primary';
        case 'RECEIVING':
          return 'info';
        case 'END':
          return 'success';
        default:
          return 'info';
      }
    },
    
    // 获取订单类型标签类型
    getOrderTypeTagType(orderType) {
      switch (orderType) {
        case '02': return 'danger';
        case '01': default: return 'success';
      }
    },
    
    // 获取订单类型文本
    getOrderTypeText(orderType) {
      switch (orderType) {
        case '02': return '退货';
        case '01': default: return '正常';
      }
    },
    
    // 获取时间线项类型
    getTimelineType(status) {
      switch (status) {
        case '已创建':
          return 'primary';
        case '已接受':
          return 'success';
        case '已发货':
          return 'warning';
        case '已完成':
          return 'success';
        case '已拒绝':
          return 'danger';
        default:
          return 'info';
      }
    },
    
    // 创建模拟历史记录
    createMockHistory() {
      const history = [];
      
      // 添加订单创建记录
      history.push({
        time: this.orderInfo.createTime,
        status: '已创建',
        content: '订单已创建'
      });
      
      // 根据当前状态，添加可能的历史记录
      const statusList = ['ACTIVE', 'EXECUTING', 'SENDING', 'RECEIVING', 'END'];
      const statusIndex = statusList.indexOf(this.orderInfo.status);
      
      if (statusIndex > 0) {
        // 如果不是第一个状态，添加中间状态的历史记录
        for (let i = 0; i < statusIndex; i++) {
          const status = statusList[i + 1];
          // 创建一个假的时间（在创建时间之后的随机时间）
          const mockTime = new Date(new Date(this.orderInfo.createTime).getTime() + (i + 1) * 24 * 60 * 60 * 1000);
          
          // 将英文状态转换为中文
          const statusMap = {
            'ACTIVE': '待确认',
            'EXECUTING': '进行中',
            'SENDING': '已发货',
            'RECEIVING': '已收货',
            'END': '已结束'
          };
          const chineseStatus = statusMap[status] || status;
          
          history.push({
            time: mockTime,
            status: chineseStatus,
            content: `订单状态变更为 ${chineseStatus}`
          });
        }
      }
      
      this.orderHistory = history;
    },
    
    // 更新订单状态
    updateOrderStatus(newStatus) {
      // 将英文状态转换为中文
      const statusMap = {
        'ACTIVE': '待确认',
        'EXECUTING': '进行中',
        'SENDING': '已发货',
        'RECEIVING': '已收货',
        'END': '已结束'
      };
      const chineseStatus = statusMap[newStatus] || newStatus;
      
      this.$confirm(`确认将订单状态更改为 "${chineseStatus}" ?`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.loading = true;
        
        update_order_status(this.orderId, newStatus)
          .then(response => {
            if (response.data && response.data.success) {
              // 将英文状态转换为中文
              const statusMap = {
                'ACTIVE': '待确认',
                'EXECUTING': '进行中',
                'SENDING': '已发货',
                'RECEIVING': '已收货',
                'END': '已结束'
              };
              const chineseStatus = statusMap[newStatus] || newStatus;
              
              this.$message.success(`订单状态已更新为 ${chineseStatus}`);
              
              // 更新本地订单状态
              this.orderInfo.status = newStatus;
              
              // 添加新的历史记录
              this.orderHistory.push({
                time: new Date(),
                status: chineseStatus,
                content: `订单状态变更为 ${chineseStatus}`
              });
            } else {
              this.$message.error(response.data?.message || '更新订单状态失败');
            }
            this.loading = false;
          })
          .catch(error => {
            console.error('更新订单状态失败:', error);
            this.$message.error('更新订单状态失败: ' + (error.message || '未知错误'));
            this.loading = false;
            
            // 开发测试用，模拟成功响应
            if (process.env.NODE_ENV === 'development') {
              // 将英文状态转换为中文
              const statusMap = {
                'ACTIVE': '待确认',
                'EXECUTING': '进行中',
                'SENDING': '已发货',
                'RECEIVING': '已收货',
                'END': '已结束'
              };
              const chineseStatus = statusMap[newStatus] || newStatus;
              
              // 模拟更新成功
              this.$message.success(`订单状态已更新为 ${chineseStatus} (测试环境)`);
              
              // 更新本地订单状态
              this.orderInfo.status = newStatus;
              
              // 添加新的历史记录
              this.orderHistory.push({
                time: new Date(),
                status: chineseStatus,
                content: `订单状态变更为 ${chineseStatus}`
              });
              
              this.loading = false;
            }
          });
      }).catch(() => {
        this.$message.info('已取消状态更新');
      });
    },
    
    downloadExcel() {
      try {
        downloadOrderExcel(this.$refs.printContent, this.orderInfo.orderTitle || this.orderInfo.orderId || '订单');
        this.$message.success('订单 Excel 下载成功');
      } catch (error) {
        console.error('下载订单 Excel 失败:', error);
        this.$message.error(error.message || '下载订单 Excel 失败');
      }
    },

    // 打印订单
    async printOrder() {
      try {
        await this.performPrint();
      } catch (error) {
        console.error('打印失败:', error);
        this.$message.error('打印失败，请检查浏览器设置');
      }
    },
    
    // 执行打印
    async performPrint() {
      try {
        // 获取打印内容
        const printContent = this.$refs.printContent.innerHTML;
// 创建打印窗口
        const printWindow = window.open('', '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');
        
        if (!printWindow) {
          this.$message.error('无法打开打印窗口，请检查浏览器弹窗设置');
          return;
        }
        
        // 写入打印内容
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>订单打印 - ${this.orderInfo.orderTitle || this.orderInfo.orderId || '订单'}</title>
            <meta charset="utf-8">
            <style>
              body { 
                font-family: Arial, "Microsoft YaHei", sans-serif; 
                margin: 20px; 
                font-size: 12px; 
                line-height: 1.4;
                color: #333;
              }
              .info-section { 
                margin-bottom: 20px; 
                border: 1px solid #ddd; 
                padding: 15px; 
                background-color: #fff;
              }
              .section-title { 
                font-size: 16px; 
                font-weight: bold; 
                margin-bottom: 15px; 
                border-bottom: 1px solid #ddd; 
                padding-bottom: 10px; 
                color: #333;
              }
              /* 布局 */
              .el-row {
                display: flex;
                flex-wrap: wrap;
                margin-left: -10px;
                margin-right: -10px;
              }
              .el-col-12 {
                width: 50%;
                padding: 0 10px;
                box-sizing: border-box;
              }
              .el-col-8 {
                width: 33.33%;
                padding: 0 10px;
                box-sizing: border-box;
              }
              
              /* 单位信息块样式 */
              .unit-info-block {
                padding: 15px;
                background-color: #f8f9fa;
                border-radius: 4px;
                border: 1px solid #e4e7ed;
                margin-bottom: 10px;
              }
              .unit-title {
                font-size: 14px;
                font-weight: 600;
                color: #303133;
                margin-bottom: 12px;
                padding-bottom: 8px;
                border-bottom: 2px solid #409EFF;
              }
              .demand-unit .unit-title {
                border-bottom-color: #67C23A;
              }
              .supplier-unit .unit-title {
                border-bottom-color: #409EFF;
              }
              
              .info-item { 
                margin-bottom: 10px; 
                display: flex;
                align-items: center;
              }
              .info-item label { 
                font-weight: bold; 
                margin-right: 10px; 
                min-width: 120px;
                color: #606266;
              }
              .info-item span {
                flex: 1;
                color: #333;
              }
              
              /* 订单状态和类型颜色 */
              .status-ACTIVE { color: #E6A23C; font-weight: 500; }
              .status-EXECUTING { color: #67C23A; font-weight: 500; }
              .status-SENDING { color: #409EFF; font-weight: 500; }
              .status-RECEIVING { color: #909399; font-weight: 500; }
              .status-END { color: #67C23A; font-weight: 500; }
              .status-REJECTED { color: #F56C6C; font-weight: 500; }
              .order-type-01 { color: #67C23A; font-weight: 500; }
              .order-type-02 { color: #F56C6C; font-weight: 500; }
              
              /* 表格样式优化 */
              table { 
                width: 100%; 
                border-collapse: collapse; 
                margin-top: 15px; 
                font-size: 10px; 
                table-layout: fixed; 
              }
              th, td { 
                border: 1px solid #ddd; 
                padding: 6px 8px; 
                text-align: left; 
                vertical-align: top;
              }
              th { 
                background-color: #f5f7fa; 
                font-weight: bold; 
                color: #606266;
              }
              
              /* 确保表格在打印时不会被压缩 */
              .materials-table, .contracts-table { 
                width: 100% !important; 
                table-layout: fixed !important;
              }
              
              /* 产品清单表格列宽优化 - 打印专用 */
              .materials-table th:nth-child(1), .materials-table td:nth-child(1) { width: 5%; } /* 序号 */
              .materials-table th:nth-child(2), .materials-table td:nth-child(2) { width: 15%; } /* 材料名称 */
              .materials-table th:nth-child(3), .materials-table td:nth-child(3) { width: 12%; } /* 规格型号 */
              .materials-table th:nth-child(4), .materials-table td:nth-child(4) { width: 10%; } /* 材质要求 */
              .materials-table th:nth-child(5), .materials-table td:nth-child(5) { width: 6%; } /* 单位 */
              .materials-table th:nth-child(6), .materials-table td:nth-child(6) { width: 8%; } /* 数量 */
              .materials-table th:nth-child(7), .materials-table td:nth-child(7) { width: 10%; } /* 不含税单价 */
              .materials-table th:nth-child(8), .materials-table td:nth-child(8) { width: 10%; } /* 含税单价 */
              .materials-table th:nth-child(9), .materials-table td:nth-child(9) { width: 10%; } /* 含税合价 */
              .materials-table th:nth-child(10), .materials-table td:nth-child(10) { width: 6%; } /* 税率 */
              .materials-table th:nth-child(11), .materials-table td:nth-child(11) { width: 8%; } /* 到货日期 */
              .materials-table th:nth-child(12), .materials-table td:nth-child(12) { width: 10%; } /* 备注 */
              
              /* 合同信息表格列宽优化 - 打印专用 */
              .contracts-table th:nth-child(1), .contracts-table td:nth-child(1) { width: 8%; } /* 序号 */
              .contracts-table th:nth-child(2), .contracts-table td:nth-child(2) { width: 35%; } /* 合同名称 */
              .contracts-table th:nth-child(3), .contracts-table td:nth-child(3) { width: 25%; } /* 合同编号 */
              .contracts-table th:nth-child(4), .contracts-table td:nth-child(4) { width: 17%; } /* 合同金额 */
              .contracts-table th:nth-child(5), .contracts-table td:nth-child(5) { width: 15%; } /* 创建时间 */
              
              /* 金额样式 */
              .amount-text, .total-amount { 
                color: #333; 
                font-weight: bold; 
              }
              
              /* 标签样式 */
              .el-tag { 
                border: 1px solid #ddd; 
                background-color: #fff; 
                color: #333; 
                padding: 2px 6px; 
                font-size: 10px; 
                border-radius: 3px;
              }
              
              /* 合计信息 */
              .total-section { 
                margin-top: 15px; 
                padding-top: 15px; 
                border-top: 1px solid #ddd; 
                text-align: right;
              }
              .total-item { 
                margin-left: 20px; 
                display: inline-block; 
                font-weight: bold;
              }
              
              /* 空数据样式 */
              .no-data {
                text-align: center;
                padding: 20px;
                color: #999;
              }
              
              @media print { 
                body { margin: 0; }
                .info-section { page-break-inside: avoid; }
                .no-print { display: none !important; }
                table { page-break-inside: avoid; }
                .el-button { display: none !important; }
              }
            </style>
          </head>
          <body>
            ${printContent}
          </body>
          </html>
        `);
        
        await installPlatformPrintFooter(printWindow, null, {
          documentType: 'ORDER',
          documentKey: this.orderId
        });
        printWindow.document.close();
        
        let printTriggered = false;
        let fallbackTimer = null;
        const triggerPrint = () => {
          if (printTriggered || printWindow.closed) return;
          printTriggered = true;
          if (fallbackTimer) clearTimeout(fallbackTimer);

          try {
            printWindow.focus();
            printWindow.print();
            
            // 打印完成后关闭窗口（延迟一点时间让用户看到打印对话框）
            setTimeout(() => {
              printWindow.close();
            }, 1000);
          } catch (printError) {
            console.error('打印执行失败:', printError);
            this.$message.error('打印执行失败，请手动打印页面');
          }
        };

        // 优先等待窗口加载完成；兜底定时器仅在加载事件未触发时执行。
        printWindow.onload = triggerPrint;

        // 如果onload没有触发，直接尝试打印
        fallbackTimer = setTimeout(triggerPrint, 500);
        
      } catch (error) {
        console.error('打印过程出错:', error);
        this.$message.error('打印失败: ' + error.message);
      }
    },
    
    // 显示发货登记弹窗
    showShipmentDialog() {
      this.shipmentDialogVisible = true;
    },
    
    // 处理发货成功
    handleShipmentSuccess(data) {
// 更新本地订单状态
      this.orderInfo.status = 'SENDING';
      
      // 添加物流信息到历史记录
      this.orderHistory.push({
        time: new Date(),
        status: '已发货',
        content: `发货登记成功 - 物流单号: ${data.trackingNumber}, 物流公司: ${data.logisticsCompany}`
      });
    },
    
    // 跳转到发货页面
    goToShipmentPage() {
      this.$router.push({
        name: 'OrderShipment',
        params: { orderId: this.orderId }
      });
    },
    
    // 返回上一页
    goBack() {
      this.$router.go(-1);
    },
    
    // 脱敏姓名
    maskName(name) {
      return maskNameUtil(name);
    },
    
    // 脱敏电话
    maskPhone(phone) {
      return phone || '';
    }
  }
};
</script>

<style lang="scss" scoped>
.supplier-order-detail-container {
  padding: 20px;
  background-color: #f0f2f5;
  min-height: calc(100vh - 120px);
}

.print-content {
  /* 打印内容区域样式 - 保持默认样式 */
  width: 100%;
}

/* 平时显示的表格样式 - 确保表格正常显示 */
.materials-table,
.contracts-table {
  /* 平时显示时使用正常的表格布局 */
  width: 100%;
}

/* 平时显示时强制表格填满整行 */
.materials-table .el-table,
.contracts-table .el-table {
  width: 100% !important;
}

.materials-table .el-table__body,
.contracts-table .el-table__body {
  width: 100% !important;
}

.materials-table .el-table__header,
.contracts-table .el-table__header {
  width: 100% !important;
}

/* 确保表格内容区域填满整行 */
.materials-table .el-table__body-wrapper,
.contracts-table .el-table__body-wrapper {
  width: 100% !important;
}

.materials-table .el-table__header-wrapper,
.contracts-table .el-table__header-wrapper {
  width: 100% !important;
}

/* 强制表格行填满整行 */
.materials-table .el-table__row,
.contracts-table .el-table__row {
  width: 100% !important;
}

/* 确保表格单元格能自适应填满 */
.materials-table .el-table__cell,
.contracts-table .el-table__cell {
  box-sizing: border-box;
}

/* 打印时的表格优化样式 - 只在打印时生效 */
@media print {
  .materials-table,
  .contracts-table {
    /* 打印时确保表格在打印时不会被压缩 */
    table-layout: fixed !important;
    width: 100% !important;
    font-size: 10px !important;
  }
  
  .materials-table th,
  .materials-table td,
  .contracts-table th,
  .contracts-table td {
    padding: 3px 4px !important;
    font-size: 10px !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
  }
  
  /* 确保表格内容完整显示 */
  .el-table__body-wrapper {
    overflow: visible !important;
  }
  
  .el-table__header-wrapper {
    overflow: visible !important;
  }
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.header-title {
  display: flex;
  align-items: center;
}

.title-mark {
  width: 4px;
  height: 20px;
  background-color: #409EFF;
  margin-right: 8px;
}

.header-title span {
  font-size: 18px;
  font-weight: bold;
  color: #333;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.info-section {
  background-color: #fff;
  padding: 20px;
  border-radius: 4px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.section-title {
  font-size: 16px;
  font-weight: 500;
  color: #333;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #ebeef5;
}

.info-item {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  font-size: 14px;
  
  label {
    font-weight: 500;
    color: #606266;
    min-width: 120px;
    margin-right: 10px;
    font-size: 14px;
  }
  
  span {
    color: #333;
    flex: 1;
    font-size: 14px;
  }
}

.unit-info-block {
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e4e7ed;
  
  .unit-title {
    font-size: 15px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 15px;
    padding-bottom: 10px;
    border-bottom: 2px solid #409EFF;
  }
  
  &.demand-unit .unit-title {
    border-bottom-color: #67C23A;
  }
  
  &.supplier-unit .unit-title {
    border-bottom-color: #409EFF;
  }
  
  .info-item {
    margin-bottom: 12px;
    
    label {
      min-width: 100px;
    }
  }
}

.amount-text {
  color: #f56c6c;
  font-weight: 500;
}

/* 订单状态颜色 */
.info-item .status-text {
  font-weight: 500;
}
.info-item .status-ACTIVE {
  color: #E6A23C !important; /* warning */
}
.info-item .status-EXECUTING {
  color: #67C23A !important; /* success */
}
.info-item .status-SENDING {
  color: #409EFF !important; /* primary */
}
.info-item .status-RECEIVING {
  color: #909399 !important; /* info */
}
.info-item .status-END {
  color: #67C23A !important; /* success */
}
.info-item .status-REJECTED {
  color: #F56C6C !important; /* danger */
}

/* 订单类型颜色 */
.info-item .order-type-text {
  font-weight: 500;
}
.info-item .order-type-01 {
  color: #67C23A !important; /* 正常订单 - success */
}
.info-item .order-type-02 {
  color: #F56C6C !important; /* 退货订单 - danger */
}

.total-section {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #ebeef5;
  
  .total-item {
    margin-left: 30px;
    display: flex;
    align-items: center;
    
    label {
      font-weight: 500;
      color: #606266;
      margin-right: 10px;
    }
    
    .total-amount {
      color: #f56c6c;
      font-weight: 500;
      font-size: 16px;
    }
  }
}

.no-data {
  text-align: center;
  padding: 40px 0;
  color: #909399;
}

.total-info {
  margin-top: 15px;
  text-align: right;
  font-weight: bold;
  padding: 10px;
  border-top: 1px solid #EBEEF5;
}

.contract-info {
  margin-top: 15px;
  font-size: 12px;
  color: #606266;
}


.action-buttons {
  display: flex;
  justify-content: center;
  padding: 20px 0;
}

.action-buttons .el-button {
  margin: 0 10px;
  min-width: 120px;
}

.status-info {
  text-align: center;
  color: #909399;
  padding: 20px 0;
}

.status-info i {
  margin-right: 5px;
}

/* 打印样式 */
@media print {
  .page-header {
    display: none;
  }
  
  .supplier-order-detail-container {
    padding: 0;
    background-color: #fff;
  }
  
  .info-section {
    box-shadow: none;
    border: 1px solid #ddd;
    margin-bottom: 15px;
    page-break-inside: avoid;
  }
  
  .el-button {
    display: none;
  }
  
  .el-tag {
    border: 1px solid #ddd;
    background-color: #fff;
    color: #333;
  }
  
  .el-table {
    border: 1px solid #ddd;
  }
  
  .el-table th,
  .el-table td {
    border: 1px solid #ddd;
  }
  
  .el-table th {
    background-color: #f5f7fa !important;
    color: #333 !important;
  }
  
  .amount-text,
  .total-amount {
    color: #333 !important;
  }
  
  /* 确保表格在打印时不会分页 */
  .el-table {
    page-break-inside: avoid;
  }
}

::v-deep .el-table th {
  background-color: #f5f7fa;
}

::v-deep .el-table--border {
  border-radius: 4px;
  overflow: hidden;
}
</style>

<style lang="scss">
.spec-tooltip {
  max-width: 600px !important;
  white-space: normal;
  word-break: break-all;
}
</style>
